import type Highcharts from 'highcharts';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CashFlowDataPointDto } from '../../api/generated/client';
import { CASHFLOW_GROSS_EXPENSE_CLASS_NAME, CASHFLOW_GROSS_INCOME_CLASS_NAME, CHART_TYPE_COLUMN, CHART_TYPE_LINE } from '../../config/constants';
import type { ICashflowSeriesForClientOutput } from '../../interfaces/projection';

export interface ICashFlowSeriesResult {
    seriesData: (Highcharts.SeriesOptionsType & { color: string })[];
    globalMin: number;
    globalMax: number;
}

export const useCashFlowSeriesForClientOutput = (cashFlow?: CashFlowDataPointDto[]): ICashFlowSeriesResult => {
    const { t } = useTranslation();

    return useMemo(() => {
        const cashflowSeries: ICashflowSeriesForClientOutput = {
            surplus: [],
            grossIncome: [],
            grossExpenses: [],
        };

        const allValues: number[] = [];

        cashFlow?.forEach((data) => {
            const year = moment(data.year, 'YYYY').valueOf();

            // Compute values for series
            const grossIncomeValue = data.grossIncome!;
            const grossExpensesValue = data.grossExpenses! * -1;
            const surplusValue = data.expenseSurplus! * -1 + data.incomeSurplus!;

            // Collect all Y values for extents calculation
            if (data.grossIncome != null) allValues.push(grossIncomeValue);
            if (data.grossExpenses != null) allValues.push(grossExpensesValue);
            if (data.expenseSurplus != null && data.incomeSurplus != null) allValues.push(surplusValue);

            // Build series data
            cashflowSeries.surplus.push({ x: year, y: surplusValue });
            cashflowSeries.grossExpenses.push({
                x: year,
                y: grossExpensesValue,
                className: CASHFLOW_GROSS_EXPENSE_CLASS_NAME,
            });
            cashflowSeries.grossIncome.push({
                x: year,
                y: grossIncomeValue,
                className: CASHFLOW_GROSS_INCOME_CLASS_NAME,
            });
        });

        const seriesData: (Highcharts.SeriesOptionsType & { color: string })[] = [
            {
                type: CHART_TYPE_COLUMN,
                name: t('grossIncome'),
                color: '#9fa5c6',
                data: cashflowSeries.grossIncome,
                pointPadding: 0,
                zIndex: 1,
            },
            {
                name: t('grossExpenses'),
                color: '#DFDDCB',
                type: CHART_TYPE_COLUMN,
                data: cashflowSeries.grossExpenses,
                pointPadding: 0,
                zIndex: 1,
            },
            {
                name: t('incomeExpenseSurplus'),
                type: CHART_TYPE_LINE,
                zIndex: 11,
                marker: {
                    enabled: false,
                },
                cursor: 'pointer',
                color: '#141e55',
                data: cashflowSeries.surplus,
            },
        ];

        return {
            seriesData,
            ...(allValues.length === 0
                ? { globalMin: 0, globalMax: 0 }
                : {
                      globalMin: Math.min(...allValues),
                      globalMax: Math.max(...allValues),
                  }),
        };
    }, [cashFlow, t]);
};
